/**
 * /app/$tenantSlug/leave/comp-off — Comp-Off balance + history (migration 063).
 *
 * Shows the caller's comp-off credits (worked_date / expires_on / status /
 * remaining) and the available total. Managers/HR get a "Grant comp-off" CTA
 * (manual grant to a report). Consumption is NOT here — an employee files a
 * normal leave request against the Comp-Off (is_comp_off) leave type.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { LeaveFeatureGate } from "@/components/leave/leave-feature-gate";
import { useLeaveRoles } from "@/components/leave/use-leave-roles";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCompOff,
  useCompOffBalance,
  useGrantCompOff,
} from "@/hooks/use-leave";
import { toApiError } from "@/lib/api";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/leave/comp-off",
)({
  component: CompOffRoute,
});

function CompOffRoute() {
  const { isApprover } = useLeaveRoles();
  const credits = useCompOff();
  const balance = useCompOffBalance();
  const grant = useGrantCompOff();

  const [userId, setUserId] = useState("");
  const [workedDate, setWorkedDate] = useState("");
  const [creditDays, setCreditDays] = useState("1");
  const [notes, setNotes] = useState("");

  async function submitGrant() {
    const uid = Number(userId);
    if (!uid || !workedDate) {
      toast.error("Employee ID and worked date are required");
      return;
    }
    try {
      await grant.mutateAsync({
        user_id: uid,
        worked_date: workedDate,
        credit_days: Number(creditDays),
        notes: notes.trim() || undefined,
      });
      toast.success("Comp-off granted");
      setUserId("");
      setWorkedDate("");
      setNotes("");
    } catch (e) {
      toast.error(toApiError(e).message ?? "Failed to grant");
    }
  }

  return (
    <TimeAndLeaveShell
      title="Comp-off"
      description="Compensatory off earned for working on a holiday or weekly-off. Use a Comp-Off leave request to consume credits."
    >
      <LeaveFeatureGate>
        <div className="space-y-6">
          <Card className="max-w-sm">
            <CardHeader>
              <CardTitle>Available comp-off</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-semibold">
                {balance.data?.available_days ?? 0}
                <span className="text-muted-foreground text-base font-normal">
                  {" "}
                  day(s)
                </span>
              </p>
            </CardContent>
          </Card>

          {isApprover && (
            <Card className="max-w-2xl">
              <CardHeader>
                <CardTitle>Grant comp-off</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="uid">Employee ID</Label>
                    <Input
                      id="uid"
                      inputMode="numeric"
                      value={userId}
                      onChange={(e) => setUserId(e.target.value)}
                      placeholder="e.g. 42"
                    />
                  </div>
                  <div>
                    <Label htmlFor="wd">Worked date</Label>
                    <Input
                      id="wd"
                      type="date"
                      value={workedDate}
                      onChange={(e) => setWorkedDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Credit</Label>
                    <Select value={creditDays} onValueChange={setCreditDays}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Full day (1.0)</SelectItem>
                        <SelectItem value="0.5">Half day (0.5)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="n">Notes (optional)</Label>
                  <Textarea
                    id="n"
                    value={notes}
                    maxLength={2000}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
                <Button onClick={submitGrant} disabled={grant.isPending}>
                  {grant.isPending ? "Granting…" : "Grant comp-off"}
                </Button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>My comp-off credits</CardTitle>
            </CardHeader>
            <CardContent>
              {credits.data && credits.data.comp_off_credits.length > 0 ? (
                <ul className="divide-y text-sm">
                  {credits.data.comp_off_credits.map((c) => (
                    <li
                      key={c.id}
                      className="flex flex-wrap items-center justify-between gap-2 py-2"
                    >
                      <span>
                        Worked {c.worked_date} · expires {c.expires_on} ·{" "}
                        {c.remaining_days} of {c.credit_days} day(s) left
                        <span className="text-muted-foreground">
                          {" "}
                          ({c.source})
                        </span>
                      </span>
                      <Badge variant="outline">{c.status}</Badge>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground text-sm">
                  No comp-off credits yet.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </LeaveFeatureGate>
    </TimeAndLeaveShell>
  );
}
